import {
  FetchProductPickerProductsQueryVariables,
  FetchProductPickerProductsWithVariantsQueryVariables,
  InputMaybe,
} from "@src/generated";
import store from "@src/store";
import {
  getProductPickerData,
  getProductPickerProducts,
  getProductPickerSelectedIds,
  PRODUCT_PICKER_PAGE_SIZE,
  PRODUCT_PICKER_VARIANTS_PAGE_SIZE,
  setProductPickerProductCount,
  setProductPickerProducts,
} from "@src/store/slices/productPicker";
import {
  ProductPickerFilterType,
  ProductPickerProduct,
} from "@src/store/slices/productPicker/types";
import { loadProductCount } from "@src/store/slices/productPicker/util/loadProductCount";
import {
  mergeSelectedIntoProducts,
  preservesSelectedProducts,
} from "@src/store/slices/productPicker/util/mergeSelectedIntoProducts";
import { loadProducts } from "@src/store/slices/productPicker/util/loadProducts";
import { loadProductsWithVariants } from "@src/store/slices/productPicker/util/loadProductsWithVariants";
import { AgGridCursorDataSource } from "@src/utils/AgGridCursorDataSource";

export class ProductPickerProductDataSource extends AgGridCursorDataSource<ProductPickerProduct> {
  private filters: Record<ProductPickerFilterType, string[]> = {
    product_type: [],
    vendor: [],
    tag: [],
    status: [],
    collection_id: [],
  };
  private search: string = "";
  private query: InputMaybe<string> | undefined = undefined;
  private readonly allowIgProducts: boolean = false;
  private readonly loadVariants: boolean = false;

  constructor(options?: {
    allowIgProducts?: boolean;
    pageSize?: number;
    loadVariants?: boolean;
  }) {
    super();
    this.allowIgProducts = options?.allowIgProducts ?? false;
    this.loadVariants = options?.loadVariants ?? false;
  }

  public setFilters(filters: Record<ProductPickerFilterType, string[]>) {
    if (JSON.stringify(filters) !== JSON.stringify(this.filters)) {
      this.filters = filters;
      this.reload();
    }
  }

  public setSearch(search: string) {
    if (search != this.search) {
      this.search = search;
      this.debouncedReload();
    }
  }

  public clear() {
    super.clear();
    this.filters = {
      product_type: [],
      vendor: [],
      tag: [],
      status: [],
      collection_id: [],
    };
    this.search = "";
    this.query = undefined;
  }

  protected async fetchData() {
    const variables = this.createVariables();
    if (variables.query !== this.query) {
      store.dispatch(setProductPickerProductCount(undefined));
      this.query = variables.query;
      void loadProductCount({ query: this.query }).then((count) => {
        store.dispatch(setProductPickerProductCount(count));
      });
    }
    const result = this.loadVariants
      ? await loadProductsWithVariants(variables, this.allowIgProducts)
      : await loadProducts(variables, this.allowIgProducts);
    this.data = result.data;
    this.endCursor = result.endCursor;
    this.hasNextPage = result.hasNextPage;
  }

  protected createVariables():
    | FetchProductPickerProductsQueryVariables
    | FetchProductPickerProductsWithVariantsQueryVariables {
    const queries: string[] = [];

    // Filter out with the "price_test" tag since shopify collection id gql query is incompatible w/ tags query
    if (
      !this.filters?.collection_id ||
      (this.filters.collection_id.length === 0 && !this.allowIgProducts)
    ) {
      queries.push("-tag:'price_test'");
    }

    if (this.search) {
      queries.push(this.search);
    }

    if (this.filters) {
      for (const [name, value] of Object.entries(this.filters)) {
        if (value && value.length > 0) {
          // Check if array exists and has items
          if (name === "collection_id") {
            // Collection ID doesn't need quotes (numeric field)
            this.addFilterClause(
              queries,
              value.map((id) => `${name}:${id}`)
            );
          } else if (name === "product_type") {
            // Use quotes for text fields to ensure exact match
            this.addFilterClause(
              queries,
              value.map((type) => `${name}:"${type}"`)
            );
          } else if (name === "tag") {
            // Handle multiple tags with OR logic - each tag as separate condition
            this.addFilterClause(
              queries,
              value.map((tag) => `${name}:"${tag}"`)
            );
          } else if (name === "vendor") {
            // Handle multiple vendors with OR logic - each vendor as separate condition
            this.addFilterClause(
              queries,
              value.map((vendor) => `${name}:"${vendor}"`)
            );
          } else if (name === "status") {
            // Handle multiple status values with OR logic
            this.addFilterClause(
              queries,
              value.map((status) => `${name}:"${status}"`)
            );
          } else {
            // Generic handler for other array-based filters
            this.addFilterClause(
              queries,
              value.map((item) => {
                if (typeof item === "string" && isNaN(Number(item))) {
                  return `${name}:"${item}"`;
                } else {
                  return `${name}:${item}`;
                }
              })
            );
          }
        }
      }
    }

    return {
      first: PRODUCT_PICKER_PAGE_SIZE,
      after: this.endCursor,
      query: queries.join(" AND "), // Space-separated for implicit AND in Shopify
      ...(this.loadVariants && {
        variantsFirst: PRODUCT_PICKER_VARIANTS_PAGE_SIZE,
      }),
    };
  }

  protected onDataUpdate() {
    const state = store.getState();
    const pickerData = getProductPickerData(state);

    if (preservesSelectedProducts(pickerData?.type)) {
      const selectedIds = new Set(getProductPickerSelectedIds(state));
      const currentProducts = getProductPickerProducts(state);
      store.dispatch(
        setProductPickerProducts(
          mergeSelectedIntoProducts(currentProducts, this.data, selectedIds)
        )
      );
    } else {
      store.dispatch(setProductPickerProducts(this.data));
    }
  }

  protected addFilterClause(queries: string[], values: string[]) {
    if (!values || values.length === 0) {
      return;
    } else if (values.length === 1) {
      queries.push(values[0]);
    } else {
      queries.push(`(${values.join(" OR ")})`);
    }
  }
}
